import { useEffect, useState, useRef } from "react";
import {
  widget,
  ChartingLibraryWidgetOptions,
  LanguageCode,
  ResolutionString,
  ThemeName,
  IChartingLibraryWidget,
  ColorGradient,
} from "../../charting_library";
import { useTheme } from "@mui/material/styles";
import { BotOrder } from "../../_app/types";

interface ChartProperties {
  symbol: string;
  locale: LanguageCode;
  debug: boolean;
  autosize: boolean;
  interval: ResolutionString;
  theme: ThemeName;
  allow_symbol_change?: boolean;
  timezone: any;
  datafeed: any;
}

interface Props {
  background: string;
  headerColor?: string;
  ChartProperties: ChartProperties;
  orders?: BotOrder[];
}

export const TradingView = ({ background, ChartProperties, headerColor, orders }: Props) => {
  const theme = useTheme();
  const grey100 = theme.palette.grey[100];
  const grey200 = theme.palette.grey[200];
  const grey300 = theme.palette.grey[300];
  const grey500 = theme.palette.grey[500];
  const grey600 = theme.palette.grey[600];
  const candlesColor = {
    short: "#F23645",
    long: "#089981",
  };
  const PColor = theme.palette.primary.main;
  const [tradingViewWidget, setTradingViewWidget] = useState<IChartingLibraryWidget | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const widgetOptions: ChartingLibraryWidgetOptions = {
    container: "chart_container",
    library_path: "/charting_library/",
    custom_css_url: "/styles/tradingview.css",
    custom_themes: {
      light: {
        color1: [
          PColor,
          PColor,
          PColor,
          PColor,
          PColor,
          PColor,
          PColor,
          PColor,
          PColor,
          PColor,
          PColor,
          PColor,
          PColor,
          PColor,
          PColor,
          PColor,
          PColor,
          PColor,
          PColor,
        ] as ColorGradient,
        color2: [
          grey500,
          grey200, // active color button when opened context menu
          grey500,
          theme.palette.text.secondary,
          grey600,
          grey600,
          grey300,
          grey100, // interval's category color
          grey300,
          grey100,
          grey300,
          grey100,
          grey300,
          grey300,
          grey300,
          grey300,
          grey300,
          theme.palette.text.secondary, // header text color
          grey300,
        ] as ColorGradient,
        color3: [
          candlesColor.short,
          candlesColor.short,
          candlesColor.short,
          candlesColor.short,
          candlesColor.short,
          candlesColor.short,
          candlesColor.short,
          candlesColor.short,
          candlesColor.short,
          candlesColor.short,
          candlesColor.short,
          candlesColor.short,
          candlesColor.short,
          candlesColor.short,
          candlesColor.short,
          candlesColor.short,
          candlesColor.short,
          candlesColor.short,
          candlesColor.short,
        ] as ColorGradient,
        color4: [
          "#fff",
          "#089981",
          "#089981",
          "#089981",
          "#089981",
          "#089981",
          "#089981",
          "#089981",
          "#089981",
          "#089981",
          "#089981",
          "#089981",
          "#089981",
          "#089981",
          "#3c9577",
          "#338066",
          "#2b6a55",
          "#225544",
          "#089981",
        ],
        color5: [
          "#fff",
          "#ffffe0",
          "#feffd1",
          "#feffc2",
          "#feffb2",
          "#feffa3",
          "#fdff94",
          "#fdff84",
          "#fdff75",
          "#fdff66",
          "#fcff47",
          "#e7ea41",
          "#d2d53b",
          "#a8aa2f",
          "#939529",
          "#7e8024",
          "#696a1e",
          "#545518",
          "#3f4012",
        ],
        color6: [
          "#fff1ff",
          "#ffe2ff",
          "#ffd4ff",
          "#ffc5ff",
          "#ffb7ff",
          "#ffa9ff",
          "#ff9aff",
          "#ff8cff",
          "#ff7dff",
          "#ff6fff",
          "#ff52ff",
          "#ea4bea",
          "#d544d5",
          "#aa37aa",
          "#953095",
          "#802980",
          "#6a226a",
          "#551b55",
          "#401540",
        ],
        color7: [
          "#eff8ff",
          "#dff1ff",
          "#cfeaff",
          "#bee3ff",
          "#aedcff",
          "#9ed5ff",
          "#8eceff",
          "#7ec7ff",
          "#6ec0ff",
          "#5db9ff",
          "#3dabff",
          "#389dea",
          "#338fd5",
          "#2972aa",
          "#246495",
          "#1f5680",
          "#19476a",
          "#143955",
          "#0f2b40",
        ],
        white: headerColor ? headerColor : background,
        black: "#000000",
      },
      dark: {
        color1: [
          PColor,
          PColor,
          PColor,
          PColor,
          PColor,
          PColor,
          PColor,
          PColor,
          PColor,
          PColor,
          PColor,
          PColor,
          PColor,
          PColor,
          PColor,
          PColor,
          PColor,
          PColor,
          PColor,
        ] as ColorGradient,
        color2: [
          grey500,
          grey500,
          grey500,
          theme.palette.text.secondary,
          grey600,
          grey600,
          grey300,
          theme.palette.text.secondary,
          grey300,
          grey100,
          grey500,
          grey100,
          grey100,
          grey100,
          grey500,
          grey500,
          grey300,
          headerColor ? headerColor : grey500,
          grey500,
        ] as ColorGradient,
        color3: [
          candlesColor.short,
          candlesColor.short,
          candlesColor.short,
          candlesColor.short,
          candlesColor.short,
          candlesColor.short,
          candlesColor.short,
          candlesColor.short,
          candlesColor.short,
          candlesColor.short,
          candlesColor.short,
          candlesColor.short,
          candlesColor.short,
          candlesColor.short,
          candlesColor.short,
          candlesColor.short,
          candlesColor.short,
          candlesColor.short,
          candlesColor.short,
        ] as ColorGradient,
        color4: [
          "#089981",
          "#089981",
          "#089981",
          "#089981",
          "#089981",
          "#089981",
          "#089981",
          "#089981",
          "#089981",
          "#089981",
          "#089981",
          "#089981",
          "#089981",
          "#089981",
          "#3c9577",
          "#338066",
          "#2b6a55",
          "#225544",
          "#089981",
        ],
        color5: [
          "#fffff0",
          "#ffffe0",
          "#feffd1",
          "#feffc2",
          "#feffb2",
          "#feffa3",
          "#fdff94",
          "#fdff84",
          "#fdff75",
          "#fdff66",
          "#fcff47",
          "#e7ea41",
          "#d2d53b",
          "#a8aa2f",
          "#939529",
          "#7e8024",
          "#696a1e",
          "#545518",
          "#3f4012",
        ],
        color6: [
          "#fff1ff",
          "#ffe2ff",
          "#ffd4ff",
          "#ffc5ff",
          "#ffb7ff",
          "#ffa9ff",
          "#ff9aff",
          "#ff8cff",
          "#ff7dff",
          "#ff6fff",
          "#ff52ff",
          "#ea4bea",
          "#d544d5",
          "#aa37aa",
          "#953095",
          "#802980",
          "#6a226a",
          "#551b55",
          "#401540",
        ],
        color7: [
          "#eff8ff",
          "#dff1ff",
          "#cfeaff",
          "#bee3ff",
          "#aedcff",
          "#9ed5ff",
          "#8eceff",
          "#7ec7ff",
          "#6ec0ff",
          "#5db9ff",
          "#3dabff",
          "#389dea",
          "#338fd5",
          "#2972aa",
          "#246495",
          "#1f5680",
          "#19476a",
          "#143955",
          "#0f2b40",
        ],
        white: headerColor ? headerColor : background,
        black: "#000000",
      },
    },
    enabled_features: ["fix_left_edge", "remove_library_container_border", "show_symbol_logos"],

    disabled_features: [
      "use_localstorage_for_settings",
      "left_toolbar",
      "timeframes_toolbar",
      "header_undo_redo",
      "edit_buttons_in_legend",
      "context_menus",
      "control_bar",
      "border_around_the_chart",
      "header_symbol_search",
      "header_fullscreen_button",
      "header_screenshot",
      "header_settings",
      "header_compare",
      "move_logo_to_main_pane",
      "symbol_info_long_description",
      "legend_context_menu",
      "legend_inplace_edit",
      "object_tree_legend_mode",
      "show_hide_button_in_legend",
      "edit_buttons_in_legend",
      "delete_button_in_legend",
      "display_legend_on_all_charts",
      "format_button_in_legend",
      "hide_main_series_symbol_from_indicator_legend",
      "pricescale_unit",
      "pricescale_currency",
      "hide_object_tree_and_price_scale_exchange_label",
      "right_bar_stays_on_scroll",
      "legend_widget",
    ],
    loading_screen: {
      backgroundColor: "transparent",
    },
    header_widget_buttons_mode: "adaptive",
    overrides: {
      "header.borderRadius": "12px",
      "paneProperties.backgroundType": "solid",
      "toolBarProperties.backgroundType": "solid",
      "backdrop.background": "red",
      "toolBarProperties.background": "#fff",
      "paneProperties.background": background ? background : theme.palette.background.default,
      "scalesProperties.textColor": theme.palette.text.secondary,
    },
    ...ChartProperties,
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        tradingViewWidget
      ) {
        tradingViewWidget.closePopupsAndDialogs();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [tradingViewWidget]);

  useEffect(() => {
    if (!containerRef.current) return;

    const tvWidget = new widget({
      ...widgetOptions,
      container: containerRef.current,
    });

    tvWidget.onChartReady(() => {
      const chart = tvWidget.chart();

      chart.onDataLoaded().subscribe(
        null,
        () => {
          if (orders && orders.length) {
            orders?.forEach((order) => {
              const timestamp = Math.floor(new Date(order.creationDate).getTime() / 1000);

              if (order.side === "SHORT") {
                chart.createShape(
                  { time: timestamp, price: order.price },
                  {
                    shape: "icon",
                    icon: 0xf0d7,
                    overrides: {
                      color: "rgb(248, 203, 203)",
                      size: 13,
                    },
                    zOrder: "top",
                  }
                );
              }

              if (order.side === "LONG") {
                chart.createShape(
                  { time: timestamp, price: order.price },
                  {
                    shape: "icon",
                    icon: 0xf0d8,
                    overrides: {
                      color: "rgba(57, 255, 20, 1)",
                      size: 13,
                    },
                    zOrder: "top",
                  }
                );
              }
            });
          }
        },
        true
      );
    });

    setTradingViewWidget(tvWidget);

    return () => {
      tvWidget.remove();
    };
  }, []);

  return (
    <div
      ref={containerRef}
      id={"chart_container"}
      style={{ height: "400px" }}
      className={"TVChartContainer"}
    />
  );
};
